import { useCallback, useContext } from 'react';
import { InputControlsContext } from '@trinity/chat-components/components/BrandInsights/context';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import { useChatTextInputControls } from './useChatTextInputControls';

type SetChatInputTextMode = 'replace' | 'append';

export type SetChatInputTextOptions = {
  focus?: boolean;
  mode?: SetChatInputTextMode;
  promptHintIdentifier?: string | null;
};

export type SetChatInputText = (value: string, options?: SetChatInputTextOptions) => void;

export function useSetChatInputText(): SetChatInputText {
  const dispatch = useContext(ChatStateDispatchContext);
  const inputControls = useContext(InputControlsContext);
  const { focusChatInputTextArea } = useChatTextInputControls();

  return useCallback(
    (value: string, options?: SetChatInputTextOptions) => {
      const { focus = false, mode = 'replace', promptHintIdentifier } = options ?? {};

      if (mode === 'append') {
        if (inputControls?.insertTextAtCursor) {
          inputControls.insertTextAtCursor(value);
        } else if (dispatch) {
          dispatch({
            type: 'input-appended',
            payload: { value },
          });
        }
      } else if (inputControls?.replaceInput) {
        inputControls.replaceInput(value);

        if (promptHintIdentifier != null && dispatch) {
          dispatch({
            type: 'input-changed',
            payload: {
              value,
              promptHintIdentifier,
            },
          });
        }
      } else if (dispatch) {
        dispatch({
          type: 'input-changed',
          payload:
            promptHintIdentifier != null
              ? { value, promptHintIdentifier }
              : {
                  value,
                },
        });
      }

      if (focus) {
        focusChatInputTextArea();
      }
    },
    [dispatch, focusChatInputTextArea, inputControls]
  );
}
